import React from 'react';

import styled from 'styled-components';

import Text from 'components/Text';
import { getSocialNetworkIcon } from 'components/UserProfileCard/utils/socialNetworkIcons';
import { useTheme } from 'hooks/useTheme';

interface SocialIconsContainerProps {
  socialNetworks: any[];
  socialLoading?: boolean;
  socialError?: string | null;
  color?: string;
  circle?: boolean;
  iconSize?: number;
  useThemeColor?: boolean;
  themeColorKey?: 'primary' | 'success' | 'warning' | 'info' | 'error';
  gap?: string;
  justifyContent?: string;
  alignItems?: string;
  className?: string;
}

const Container = styled.div<{ gap?: string; justifyContent?: string; alignItems?: string }>`
  display: flex;
  justify-content: ${({ justifyContent }) => justifyContent || 'center'};
  align-items: ${({ alignItems }) => alignItems || 'center'};
  gap: ${({ gap }) => gap || '10px'};
  flex-wrap: wrap;
`;

const SocialIconsContainer: React.FC<SocialIconsContainerProps> = ({
  socialNetworks,
  socialLoading = false,
  socialError = null,
  color,
  circle = false,
  iconSize = 12,
  useThemeColor = false,
  themeColorKey = 'primary',
  gap = '10px',
  justifyContent = 'center',
  alignItems = 'center',
  className,
}) => {
  const { theme } = useTheme();

  const IconWrapper = styled.div<{ circle?: boolean; color?: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ circle }) => (circle ? '2rem' : 'auto')};
    height: ${({ circle }) => (circle ? '2rem' : 'auto')};
    border-radius: ${({ circle }) => (circle ? '50%' : '0')};
    background-color: ${({ circle }) => {
      if (!circle) return 'transparent';
      return theme.primary;
    }};
    transition: all 0.2s ease-in-out;
    cursor: pointer;

    color: ${({ circle, color }) => {
      if (circle) {
        return theme.background.primary;
      }
      return color === 'primary' ? theme.primary : 'transparent';
    }};

    svg {
      color: ${({ circle, color }) => {
        if (circle) {
          return theme.background.primary;
        }
        return color === 'primary' ? theme.primary : 'transparent';
      }};
    }

    &:hover {
      transform: scale(1.1);
      opacity: 0.9;
    }
  `;

  return (
    <Container
      gap={gap}
      justifyContent={justifyContent}
      alignItems={alignItems}
      className={className}
    >
      {socialLoading && <Text>Carregando redes sociais...</Text>}
      {socialError && <Text color="red">{socialError}</Text>}

      {!socialLoading &&
        !socialError &&
        socialNetworks
          .filter(network => network.social_network_username)
          .map(network => {
            // For WEBSITE type, use the username as direct URL, otherwise construct social media URL
            const url = network.social_network_type === 'WEBSITE'
              ? network.social_network_username.startsWith('http')
                ? network.social_network_username
                : `https://${network.social_network_username}`
              : `https://${network.social_network_type.toLowerCase()}.com/${network.social_network_username}`;

            return (
              <a
                key={network.id}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <IconWrapper circle={circle} color={color}>
                  {getSocialNetworkIcon(network.social_network_type, {
                    color: circle ? theme.background.primary : color,
                    size: iconSize,
                    useThemeColor,
                    themeColorKey,
                  })}
                </IconWrapper>
              </a>
            );
          })}
    </Container>
  );
};

export default SocialIconsContainer;
